import { useEffect, useMemo } from "react";
import {
  buildStickyWatermarkScope,
  mergeResolvedWithStickyWatermark,
  readStickyWatermarkLogoUrl,
  removeLegacyStickyWatermarkLogoUrl,
  writeStickyWatermarkLogoUrl,
  clearStickyWatermarkLogoUrl,
} from "@/components/branding/brandingCacheSync";
import { resolveWatermarkLogoUrl } from "@/components/branding/brandingUtils";
import { useResolvedBranding } from "@/components/branding/useResolvedBranding";
import { useAuth } from "@/context/AuthContext";
import type { EffectiveBranding } from "@/services/api/agentic-v2/types";

export { clearStickyWatermarkLogoUrl };

/**
 * Runtime watermark branding for the OS shell.
 * Keeps the last known logo_url so brief cache/refetch glitches do not hide the watermark.
 */
export function useOsWatermarkBranding() {
  const { user } = useAuth();
  const stickyScope = useMemo(() => buildStickyWatermarkScope(user), [user?.id, user?.company_id]);
  const query = useResolvedBranding();

  useEffect(() => {
    removeLegacyStickyWatermarkLogoUrl();
  }, []);

  const resolvedLogoUrl = resolveWatermarkLogoUrl(query.data);
  if (resolvedLogoUrl && stickyScope) {
    writeStickyWatermarkLogoUrl(stickyScope, resolvedLogoUrl);
  }

  const branding = useMemo((): EffectiveBranding | undefined => {
    if (!query.data) return undefined;
    return mergeResolvedWithStickyWatermark(query.data, stickyScope);
  }, [query.data, stickyScope]);

  useEffect(() => {
    if (!stickyScope) return;
    if (query.data?.watermark?.enabled === false) {
      clearStickyWatermarkLogoUrl(stickyScope);
      return;
    }
    if (!query.data?.watermark?.enabled) return;
    if (resolveWatermarkLogoUrl(query.data) || readStickyWatermarkLogoUrl(stickyScope)) return;
    void query.refetch();
  }, [query.data, query.refetch, stickyScope]);

  return {
    branding,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}
